import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Thermometer,
  ShieldCheck,
  FileSearch,
  Target,
  Quote,
} from "lucide-react";
import Marquee from "@/components/Marquee";
import { FadeIn, MaskLine, Eyebrow } from "@/components/Reveal";

const IMG = {
  hero: "https://images.unsplash.com/photo-1625516838246-ff33acad73ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w8NjY2NzF8MHwxfHNlYXJjaHw0fHxuaWdodCUyMHN0YXJyeSUyMHNreSUyMGZpZWxkfGVufDB8fHx8MTc4ODU4NjAzMHww&ixlib=rb-4.1.0&q=85",
  sambar: "https://images.unsplash.com/photo-1700410426386-704d39c87828?q=85&w=1200&auto=format&fit=crop",
  fox: "https://images.unsplash.com/photo-1621207184138-1806f6f5ecbb?q=85&w=1200&auto=format&fit=crop",
  rabbit: "https://images.unsplash.com/photo-1623686070581-5d2eb8187fc2?q=85&w=1200&auto=format&fit=crop",
  property: "https://images.unsplash.com/photo-1771518701790-9094cc6bd106?q=85&w=2000&auto=format&fit=crop",
  nightPaddock:
    "https://images.unsplash.com/photo-1609645778471-613f21fcf3df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w8NjY2NzF8MHwxfHNlYXJjaHwxfHxuaWdodCUyMHN0YXJyeSUyMHNreSUyMGZpZWxkfGVufDB8fHx8MTc4ODU4NjAzMHww&ixlib=rb-4.1.0&q=85",
  deer: "https://images.unsplash.com/photo-1516934024742-b461fba47600?q=85&w=1600&auto=format&fit=crop",
  outback:
    "https://images.unsplash.com/photo-1774695474322-d5a7b6cfe242?q=85&w=2000&auto=format&fit=crop",
};

const TARGETS = [
  { img: IMG.sambar, label: "Sambar Deer", alt: "Sambar stag front-on, full antlers", shift: "mb-16" },
  { img: IMG.fox, label: "Red Fox", alt: "European red fox on the move", shift: "mb-28" },
  { img: IMG.rabbit, label: "European Rabbit", alt: "European wild rabbit front-on in grass", shift: "mb-6" },
];

const CREDENTIALS = [
  "Cat A·B·D Firearms Licence",
  "Commercial Operator Licence",
  "Thermal + Night Optics",
  "$20M Public Liability",
  "Humane Dispatch Certified",
  "ACUP Chemical Permit",
];

const CHAPTERS = [
  {
    num: "01",
    title: "Fieldcraft first",
    body: "We read sign, wind and moon phase before anything leaves the safe. Every program starts on foot — pads, scats, rubs and damage mapped across your country, not guessed from the ute window.",
  },
  {
    num: "02",
    title: "Humane by code",
    body: "Single-shot dispatch, every time, no exceptions. Our operators are certified to humane destruction standards and every animal is accounted for. Welfare isn't a footnote — it's the operating procedure.",
  },
  {
    num: "03",
    title: "Accountable to the acre",
    body: "GPS-logged nights, tallied returns and a written report after every visit. You'll know exactly what was taken, where, and what it means for next season's pressure on your place.",
  },
];

const SERVICES = [
  {
    icon: Target,
    title: "Feral Animal Control",
    body: "Pigs, deer, foxes, rabbits, wild dogs, goats and cats — taken with thermal night optics, trapping and baiting under full Victorian licensing.",
    img: IMG.deer,
    span: "md:col-span-2",
  },
  {
    icon: ShieldCheck,
    title: "Farm Culling Programs",
    body: "Scheduled knockdown shoots and ongoing maintenance contracts that protect stock, crops and pasture through every season.",
    img: IMG.outback,
    span: "",
  },
  {
    icon: FileSearch,
    title: "Property Assessments",
    body: "On-site audits, damage mapping and 12-month integrated management plans with permit paperwork handled for you.",
    img: IMG.property,
    span: "md:col-span-3",
  },
];

const SPECIES = [
  ["SUS-01", "Feral Pig", "feral-pig"],
  ["RUS-02", "Sambar Deer", "sambar-deer"],
  ["CER-03", "Red Deer", "red-deer"],
  ["DAM-04", "Fallow Deer", "fallow-deer"],
  ["AXI-05", "Hog Deer", "hog-deer"],
  ["VUL-06", "European Fox", "european-fox"],
  ["ORY-07", "Rabbit", "european-rabbit"],
  ["LEP-08", "Hare", "hare"],
  ["CAN-09", "Wild Dog", "wild-dog"],
  ["CAP-10", "Feral Goat", "feral-goat"],
  ["FEL-11", "Feral Cat", "feral-cat"],
  ["MAC-12", "Kangaroo", "kangaroo"],
];

const TESTIMONIALS = [
  {
    quote:
      "Two nights of thermal work knocked the pig mobs right back. First lambing in three years without losses to dogs or foxes.",
    name: "Grain & sheep producer",
    place: "Western District, VIC",
  },
  {
    quote:
      "Professional, quiet and completely safe around the house paddock. The written report after each visit is worth every cent.",
    name: "Vineyard manager",
    place: "Yarra Valley, VIC",
  },
  {
    quote:
      "They handled the permits, the plan and the cull. Deer numbers on our bush blocks are finally under control.",
    name: "Station manager",
    place: "Gippsland, VIC",
  },
];

function Hero() {
  const ref = useRef(null);
  const [thermal, setThermal] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="home-hero"
      className="relative flex min-h-screen cursor-crosshair items-end overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10 scale-110">
        <img
          src={IMG.hero}
          alt="Starry night sky over an Australian paddock"
          className={`h-full w-full object-cover transition-[filter] duration-700 ${thermal ? "thermal-img" : ""}`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/60 to-obsidian/40" />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: 48 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        className="pointer-events-none absolute bottom-24 right-8 top-28 z-10 hidden items-end gap-4 lg:flex"
        data-testid="hero-target-frames"
      >
        {TARGETS.map((t, i) => (
          <motion.div
            key={t.label}
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15 + i * 0.18, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className={`relative h-[46vh] w-32 overflow-hidden border border-thermal/25 xl:w-44 ${t.shift}`}
          >
            <img
              src={t.img}
              alt={t.alt}
              className={`h-full w-full object-cover transition-[filter] duration-700 ${thermal ? "thermal-img" : ""}`}
            />
            <p className="absolute bottom-3 left-1/2 -translate-x-1/2 whitespace-nowrap bg-obsidian/70 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.25em] text-thermal backdrop-blur">
              {t.label}
            </p>
          </motion.div>
        ))}
      </motion.div>

      <AnimatePresence>
        {thermal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="pointer-events-none absolute inset-0 z-10"
            data-testid="thermal-overlay"
          >
            <div className="scan-line absolute left-0 h-px w-full bg-thermal/60" />
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="pulse-ring h-40 w-40 rounded-full border border-thermal/70" />
              <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-thermal/40" />
              <div className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-thermal/40" />
            </div>
            <p className="absolute right-6 top-24 font-mono text-[10px] uppercase tracking-[0.3em] text-thermal">
              TI 0.87 · SPECTRUM RED HOT · LOCK
            </p>
            <p className="absolute bottom-24 right-6 font-mono text-[10px] uppercase tracking-[0.3em] text-thermal">
              37.42°S 144.71°E · RNG 214M
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div style={{ opacity: fade }} className="relative z-20 mx-auto w-full max-w-7xl px-4 pb-24 pt-40 sm:px-6 lg:px-8">
        <MaskLine delay={0.15}>
          <span className="font-mono text-xs uppercase tracking-[0.35em] text-thermal">
            VIC &amp; Southern NSW — Licensed Field Operations
          </span>
        </MaskLine>
        <h1 className="mt-6 font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-bone sm:text-5xl lg:text-6xl" data-testid="hero-heading">
          <MaskLine delay={0.3}>Feral animals</MaskLine>
          <MaskLine delay={0.42}>don't sleep.</MaskLine>
          <MaskLine delay={0.54}>
            <span className="text-thermal">Neither do we.</span>
          </MaskLine>
        </h1>
        <FadeIn delay={0.8} className="mt-8 max-w-xl">
          <p className="text-base leading-relaxed text-euca sm:text-lg">
            Licensed, insured and field-proven pest &amp; game control across Victoria —
            thermal night shooting, farm culling programs and property assessments for
            stations, farms and peri-urban land.
          </p>
        </FadeIn>
        <FadeIn delay={0.95} className="mt-10 flex flex-wrap items-center gap-4">
          <Link
            to="/services"
            data-testid="hero-explore-services-button"
            className="group flex items-center gap-2 border border-thermal bg-thermal px-6 py-3 font-mono text-xs font-medium uppercase tracking-[0.2em] text-obsidian transition-all duration-300 hover:bg-transparent hover:text-thermal active:scale-95"
          >
            Explore Services
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/calculator"
            data-testid="hero-assessment-tool-button"
            className="flex items-center gap-2 border border-bone/30 px-6 py-3 font-mono text-xs font-medium uppercase tracking-[0.2em] text-bone transition-all duration-300 hover:border-thermal hover:text-thermal active:scale-95"
          >
            Scope My Property
          </Link>
          <button
            onClick={() => setThermal(!thermal)}
            data-testid="thermal-mode-toggle"
            className={`flex items-center gap-2 border px-4 py-3 font-mono text-[10px] uppercase tracking-[0.25em] transition-all duration-300 active:scale-95 ${
              thermal
                ? "border-thermal bg-thermal/15 text-thermal"
                : "border-bone/20 text-euca hover:border-thermal/50 hover:text-thermal"
            }`}
          >
            <Thermometer className="h-4 w-4" />
            Thermal View {thermal ? "On" : "Off"}
          </button>
        </FadeIn>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 left-1/2 z-20 -translate-x-1/2"
      >
        <div className="flex h-10 w-6 items-start justify-center border border-bone/30 p-1.5">
          <motion.div
            animate={{ y: [0, 14, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            className="h-2 w-px bg-thermal"
          />
        </div>
      </motion.div>
    </section>
  );
}

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />

      <section className="border-b border-thermal/15" data-testid="credentials-strip">
        <div className="mx-auto grid max-w-7xl grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
          {CREDENTIALS.map((c, i) => (
            <FadeIn key={c} delay={i * 0.06} className="border-r border-thermal/10 p-5 last:border-r-0">
              <p className="font-mono text-[10px] uppercase leading-relaxed tracking-[0.15em] text-euca">{c}</p>
            </FadeIn>
          ))}
        </div>
      </section>

      <Marquee
        items={[
          "Feral Pig Eradication",
          "Thermal Night Operations",
          "Red & Fallow Deer Control",
          "Fox Baiting Programs",
          "Property Assessments",
          "Rabbit Ripping & Fumigation",
          "Wild Dog Management",
          "Licensed · Insured · Humane",
        ]}
      />

      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8" data-testid="manifesto-section">
        <FadeIn>
          <Eyebrow>The Standard We Work To</Eyebrow>
          <h2 className="mt-4 max-w-2xl font-display text-2xl font-bold tracking-tight text-bone sm:text-3xl lg:text-4xl">
            Three rules. Zero shortcuts.
          </h2>
        </FadeIn>
        <div className="mt-16 space-y-0">
          {CHAPTERS.map((ch, i) => (
            <FadeIn key={ch.num} delay={i * 0.1}>
              <div className="grid gap-6 border-t border-thermal/15 py-12 md:grid-cols-12">
                <p className="font-display text-6xl font-black leading-none text-thermal/20 md:col-span-3 lg:text-7xl">
                  {ch.num}
                </p>
                <div className="md:col-span-9">
                  <h3 className="font-display text-xl font-semibold tracking-tight text-bone sm:text-2xl">{ch.title}</h3>
                  <p className="mt-4 max-w-2xl text-base leading-relaxed text-euca">{ch.body}</p>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      <section className="border-t border-thermal/15 bg-field/40" data-testid="services-bento">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
          <FadeIn className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Eyebrow>What We Do</Eyebrow>
              <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-bone sm:text-3xl lg:text-4xl">
                Three pillars of control
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="services-view-all-link"
              className="group flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-thermal"
            >
              Full breakdown
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </FadeIn>

          <div className="mt-14 grid gap-4 md:grid-cols-3">
            {SERVICES.map((s, i) => (
              <FadeIn key={s.title} delay={i * 0.12} className={s.span}>
                <Link
                  to="/services"
                  data-testid={`service-card-${i + 1}`}
                  className="group relative block h-full overflow-hidden border border-thermal/15 bg-field transition-colors duration-500 hover:border-thermal/50"
                >
                  <div className="relative aspect-[16/7] overflow-hidden">
                    <img
                      src={s.img}
                      alt={s.title}
                      className="h-full w-full object-cover grayscale-[0.6] transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-field via-field/30 to-transparent" />
                  </div>
                  <div className="p-6 sm:p-8">
                    <s.icon className="h-6 w-6 text-thermal" />
                    <h3 className="mt-4 font-display text-xl font-semibold tracking-tight text-bone sm:text-2xl">
                      {s.title}
                    </h3>
                    <p className="mt-3 max-w-lg text-sm leading-relaxed text-euca sm:text-base">{s.body}</p>
                  </div>
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8" data-testid="species-matrix">
        <FadeIn>
          <Eyebrow>Target Matrix</Eyebrow>
          <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-bone sm:text-3xl lg:text-4xl">
            Twelve targets. One standard of result.
          </h2>
        </FadeIn>
        <div className="mt-14 grid grid-cols-2 gap-px border border-thermal/15 bg-thermal/15 sm:grid-cols-4 lg:grid-cols-6">
          {SPECIES.map(([code, name, anchor], i) => (
            <FadeIn key={code} delay={i * 0.05}>
              <Link
                to={`/gallery#${anchor}`}
                data-testid={`species-cell-${code.toLowerCase()}`}
                className="group flex h-full min-h-36 flex-col justify-between bg-obsidian p-5 transition-colors duration-300 hover:bg-olive"
              >
                <p className="font-mono text-[10px] tracking-[0.2em] text-thermal/70">{code}</p>
                <p className="mt-8 font-display text-sm font-bold uppercase leading-tight tracking-wide text-bone transition-colors duration-300 group-hover:text-thermal">
                  {name}
                </p>
              </Link>
            </FadeIn>
          ))}
        </div>
      </section>

      <section className="relative overflow-hidden border-y border-thermal/15" data-testid="field-spotlight">
        <div className="relative aspect-[16/8] min-h-[420px]">
          <img src={IMG.nightPaddock} alt="Night operations over open farmland" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/40 to-transparent" />
          <div className="absolute left-5 top-5 h-10 w-10 border-l-2 border-t-2 border-thermal/70" />
          <div className="absolute right-5 top-5 h-10 w-10 border-r-2 border-t-2 border-thermal/70" />
          <div className="absolute bottom-5 left-5 h-10 w-10 border-b-2 border-l-2 border-thermal/70" />
          <div className="absolute bottom-5 right-5 h-10 w-10 border-b-2 border-r-2 border-thermal/70" />
          <div className="absolute inset-0 flex items-center">
            <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
              <FadeIn>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-thermal">
                  Pest Intelligence · Know Your Pressure
                </p>
                <h2 className="mt-4 max-w-xl font-display text-2xl font-bold tracking-tight text-bone sm:text-3xl lg:text-4xl">
                  Know what's eating your profit.
                </h2>
                <Link
                  to="/gallery"
                  data-testid="spotlight-gallery-link"
                  className="group mt-8 inline-flex items-center gap-2 border border-thermal px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-thermal transition-all duration-300 hover:bg-thermal hover:text-obsidian active:scale-95"
                >
                  Meet The Targets
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8" data-testid="testimonials-section">
        <FadeIn>
          <Eyebrow>Word From The Paddock</Eyebrow>
          <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-bone sm:text-3xl lg:text-4xl">
            Landholders who called us in
          </h2>
        </FadeIn>
        <div className="mt-14 grid gap-4 md:grid-cols-3">
          {TESTIMONIALS.map((t, i) => (
            <FadeIn key={t.place} delay={i * 0.12}>
              <figure className="flex h-full flex-col justify-between border border-thermal/15 bg-field p-8">
                <div>
                  <Quote className="h-5 w-5 text-thermal" />
                  <blockquote className="mt-5 text-base leading-relaxed text-bone/90">{t.quote}</blockquote>
                </div>
                <figcaption className="mt-8">
                  <p className="font-display text-sm font-bold uppercase tracking-wide text-bone">{t.name}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-thermal">{t.place}</p>
                </figcaption>
              </figure>
            </FadeIn>
          ))}
        </div>
      </section>

      <section className="border-t border-thermal/15 bg-field/60" data-testid="home-cta">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
          <FadeIn>
            <Eyebrow>Ready When You Are</Eyebrow>
            <h2 className="mt-4 max-w-3xl font-display text-2xl font-bold leading-tight tracking-tight text-bone sm:text-3xl lg:text-4xl">
              Feral pressure on your place? Let's put a number on it — and a plan behind it.
            </h2>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="cta-contact-button"
                className="group flex items-center gap-2 border border-thermal bg-thermal px-6 py-3 font-mono text-xs font-medium uppercase tracking-[0.2em] text-obsidian transition-all duration-300 hover:bg-transparent hover:text-thermal active:scale-95"
              >
                Request An Assessment
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a
                href="tel:+61466747214"
                data-testid="cta-phone-button"
                className="flex items-center gap-2 border border-bone/30 px-6 py-3 font-mono text-xs font-medium uppercase tracking-[0.2em] text-bone transition-all duration-300 hover:border-thermal hover:text-thermal active:scale-95"
              >
                0466 747 214
              </a>
            </div>
          </FadeIn>
        </div>
      </section>
    </main>
  );
}
